// ERP app registry — single source of truth for launcher tiles, workspace
// sidebars, permissions and coming-soon planned modules (ERP Phase 1).
import {
  TrendingUp, Receipt, Users, KanbanSquare, BarChart3, Target, GraduationCap, Award,
  CalendarDays, Megaphone, MessageSquare, FolderOpen, Gamepad2, FileSignature, HelpCircle,
  LifeBuoy, Recycle, Globe, Shield, Settings, LayoutGrid, Table2, IndianRupee, FileText,
  Webhook, History, Network, ShieldCheck, ScrollText, ListTodo, LayoutTemplate, SlidersHorizontal,
  UserRound, Hourglass, AlarmClock, BookOpenCheck, BadgeCheck, Wallet, ReceiptIndianRupee,
  LayoutDashboard, UserMinus, CalendarCheck, UsersRound, UserCheck, Building2, BookMarked,
} from 'lucide-react';
import logger from './logger';

export const CATEGORIES = [
  { key: 'core', label: 'Core ERP Apps' },
  { key: 'common', label: 'Common Apps' },
  { key: 'utility', label: 'Utility' },
];

// Admin Panel items are gated by server-derived feature grants (ADMIN_PANEL.<feature>.view),
// never by the legacy module matrix. `auth` is the AuthContext value.
const adminFeature = (featureKey, action = 'view') =>
  (hasPerm, user, auth) => Boolean(auth?.canFeature?.('ADMIN_PANEL', featureKey, action));

// item.perm = [module, action] checked via hasPerm, OR a function (hasPerm, user, auth) => bool
// item.soon = disabled coming-soon entry
export const APPS = [
  // ---------------- CORE ERP ----------------
  {
    key: 'sales', name: 'Sales', icon: TrendingUp, category: 'core', path: '/sales', live: true,
    description: 'Leads, pipeline, quotations, meetings & incentives',
    nav: [
      { group: 'Pipeline', items: [
        { label: 'Kanban Board', path: '/sales', icon: LayoutGrid, end: true },
        { label: 'Lead Log', path: '/sales/leads', icon: Table2 },
        { label: 'Dashboard', path: '/sales/dashboard', icon: BarChart3 },
        { label: 'Incentives', path: '/sales/incentives', icon: IndianRupee },
        { label: 'Projections', path: '/sales/projections', icon: TrendingUp },
      ] },
      { group: 'Documents & Meetings', items: [
        { label: 'Quotations', path: '/sales/quotations', icon: FileText, perm: ['quotations', 'view'] },
        { label: 'Meetings', path: '/sales/meetings', icon: CalendarDays, perm: ['meetings', 'view'] },
      ] },
      { group: 'Operations', items: [
        { label: 'Activity Log', path: '/sales/activity', icon: History, perm: ['activity', 'view'] },
        { label: 'Integrations', path: '/sales/integrations', icon: Webhook, perm: ['integrations', 'view'] },
        { label: 'Settings', path: '/sales/settings', icon: Settings, perm: ['settings', 'view'] },
      ] },
    ],
  },
  {
    key: 'accounting', name: 'Accounting', icon: Receipt, category: 'core', path: '/accounting', live: false,
    description: 'Invoices, bills, receipts, expenses & financial reports',
    perm: (hasPerm, user) => ['Super Admin', 'Admin', 'Manager'].includes(user?.access_role),
    planned: ['Billing', 'Customer Invoices', 'Vendor Bills', 'Receipts', 'Expenses', 'Assets', 'Budgets',
      'Financial Reports', 'Customer Profitability', 'Manual Entries', 'Extra Work to Invoice', 'Accounting Settings'],
  },
  {
    key: 'hrms', name: 'HRMS', icon: Users, category: 'core', path: '/hrms', live: true,
    description: 'Employees, directory, org chart, roles & permissions',
    perm: (hasPerm) => hasPerm('team', 'view'),
    nav: [
      { group: 'Employees & Organisation', items: [
        { label: 'Staff Directory', path: '/hrms/employees', icon: Users, perm: ['team', 'view'], end: true },
        { label: 'Add Employee', path: '/hrms/employees/new', icon: UserRound, perm: ['team', 'manage'] },
        { label: 'My Profile', path: '/hrms/my-profile', icon: UserRound },
        { label: 'Organisation Chart', path: '/hrms/org-chart', icon: Network, perm: ['team', 'view'] },
        { label: 'Positions & Designations', path: '/hrms/positions', icon: BadgeCheck, perm: ['masters', 'view'] },
        { label: 'Bulk Import', path: '/hrms/import', icon: Table2, perm: ['team', 'manage'] },
      ] },
      { group: 'Policies & Setup', items: [
        { label: 'Policy Centre', path: '/hrms/policy-centre', icon: BookOpenCheck, perm: ['masters', 'view'] },
        { label: 'Payroll Setup', path: '/hrms/payroll-setup', icon: Wallet, perm: ['team', 'manage'] },
        { label: 'Compliance Calendar', path: '/hrms/compliance', icon: CalendarCheck },
        { label: 'Work Schedules & Policies', path: '/hrms/policies', icon: CalendarDays, perm: ['masters', 'view'] },
        { label: 'Holiday Calendar', path: '/hrms/holiday-calendars', icon: CalendarDays, perm: ['masters', 'view'] },
      ] },
      { group: 'HR Operations', items: [
        { label: 'HR Dashboard', path: '/hrms/hr-dashboard', icon: LayoutDashboard },
      ] },
      // Phase 2 · Task 1: one Attendance hub (web check-in + branch engine + requests +
      // insights + integrations). /hrms/attendance-centre redirects here.
      { group: 'Time & Leave', items: [
        { label: 'Attendance', path: '/hrms/attendance', icon: AlarmClock, perm: ['team', 'view'] },
        { label: 'Leave Tracker', path: '/hrms/leave', icon: Hourglass, perm: ['team', 'view'] },
      ] },
      { group: 'People Ops', items: [
        { label: 'Recruitment', path: '/hrms/recruitment', icon: Users, perm: ['team', 'view'] },
        { label: 'Induction', path: '/hrms/induction', icon: BookOpenCheck, perm: ['team', 'view'] },
        { label: 'Lifecycle', path: '/hrms/lifecycle', icon: History, perm: ['team', 'view'] },
        { label: 'Exit Management', path: '/hrms/exits', icon: UserMinus, perm: ['team', 'manage'] },
      ] },
      { group: 'Pay & Claims', items: [
        { label: 'Payroll', path: '/hrms/payroll', icon: Wallet, perm: ['team', 'manage'] },
        { label: 'Reimbursements', path: '/hrms/reimbursements', icon: ReceiptIndianRupee },
      ] },
    ],
  },
  {
    key: 'projects', name: 'Projects', icon: KanbanSquare, category: 'core', path: '/projects', live: true,
    description: 'Client projects, services, tasks, milestones & extra work',
    perm: (hasPerm) => hasPerm('projects', 'view'),
    nav: [
      { group: 'Projects', items: [
        { label: 'All Projects', path: '/projects', icon: KanbanSquare, end: true },
        { label: 'My Tasks', path: '/projects/my-tasks', icon: ListTodo },
        { label: 'My Calendar', path: '/projects/my-calendar', icon: CalendarDays },
        { label: 'Billing Requests', path: '/projects/billing-requests', icon: IndianRupee },
      ] },
      { group: 'Setup', items: [
        { label: 'Templates', path: '/projects/settings/templates', icon: LayoutTemplate },
        { label: 'Statuses', path: '/projects/settings/statuses', icon: SlidersHorizontal },
      ] },
      { group: 'Coming Soon', items: [
        { label: 'Files & Documents', soon: true }, { label: 'Timesheets & Budgets', soon: true },
      ] },
    ],
  },
  {
    key: 'reporting', name: 'Reporting', icon: BarChart3, category: 'core', path: '/reporting', live: false,
    description: 'Client reports across Meta, Google Ads & SEO',
    planned: ['Client Reports', 'Meta Business Suite', 'Meta Ad Accounts', 'Google Ads Accounts', 'SEO Properties', 'Report Sharing'],
  },
  // ---------------- COMMON ----------------
  {
    key: 'kra', name: 'KRA & KPI', icon: Target, category: 'common', path: '/kra', live: false,
    description: 'Role scorecards, targets & performance bands',
    planned: ['Role Templates', 'Monthly Scorecards', 'Manager Validation', 'Evidence Tracking'],
  },
  {
    key: 'training', name: 'Training & SOPs', icon: GraduationCap, category: 'common', path: '/training', live: false,
    description: 'SOP library, training modules & acknowledgements',
    planned: ['SOP Library', 'Training Modules', 'Completion Tracking', 'Acknowledgements'],
  },
  {
    key: 'rewards', name: 'Rewards & Recognition', icon: Award, category: 'common', path: '/rewards', live: false,
    description: 'Badges, shout-outs & monthly awards',
    planned: ['Badges', 'Shout-outs', 'Monthly Awards', 'Leaderboards'],
  },
  {
    key: 'calendar', name: 'Calendar', icon: CalendarDays, category: 'common', path: '/calendar', live: false,
    description: 'Company events, team calendars & holidays',
    planned: ['Company Calendar', 'Team Events', 'Meeting Sync', 'Holiday List'],
  },
  {
    key: 'announcements', name: 'Announcements', icon: Megaphone, category: 'common', path: '/announcements', live: true,
    description: 'Company news, SOP updates & celebrations',
    nav: [{ group: 'Announcements', items: [
      { label: 'All Announcements', path: '/announcements', icon: Megaphone, end: true },
    ] }],
  },
  {
    key: 'chats', name: 'Chats', icon: MessageSquare, category: 'common', path: '/chats', live: false,
    description: 'Department, team & project channels',
    planned: ['Department Channels', 'Team Channels', 'Project Channels', 'Direct Messages'],
  },
  {
    key: 'documents', name: 'Documents', icon: FolderOpen, category: 'common', path: '/documents', live: false,
    description: 'Shared files, client folders & access control',
    planned: ['Shared Drive', 'Client Folders', 'Version History', 'Access Control'],
  },
  {
    key: 'games', name: 'Games', icon: Gamepad2, category: 'common', path: '/games', live: false,
    description: 'Team challenges, badges & leaderboards',
    planned: ['Team Challenges', 'Badges', 'Leaderboards'],
  },
  {
    key: 'contracts', name: 'Contract Signs', icon: FileSignature, category: 'common', path: '/contracts', live: false,
    description: 'Contract templates, e-signatures & expiry alerts',
    planned: ['Contract Templates', 'E-Signatures', 'Expiry Reminders'],
  },
  {
    key: 'help', name: 'Help / FAQ', icon: HelpCircle, category: 'common', path: '/help', live: false,
    description: 'Knowledge base, how-to guides & FAQs',
    planned: ['Knowledge Base', 'How-to Guides', 'FAQs'],
  },
  {
    key: 'support', name: 'Support / Tickets', icon: LifeBuoy, category: 'common', path: '/support', live: false,
    description: 'Raise and track internal support tickets',
    planned: ['Raise Tickets', 'Ticket Queue', 'SLAs'],
  },
  {
    key: 'recycle', name: 'Recycle Bin', icon: Recycle, category: 'common', path: '/recycle-bin', live: true,
    description: 'Restore archived records or purge them safely',
    nav: [{ group: 'Recycle Bin', items: [
      { label: 'Archived Records', path: '/recycle-bin', icon: Recycle, end: true },
    ] }],
  },
  // ---------------- UTILITY ----------------
  {
    key: 'client-portal', name: 'Client Portal', icon: Globe, category: 'utility', path: '/client-portal', live: false,
    description: 'Controlled client access to shared work',
    perm: (hasPerm, user) => ['Super Admin', 'Admin'].includes(user?.access_role),
    planned: ['Client Projects', 'Shared Files', 'Reports', 'Invoices & Payments', 'Comments & Requests'],
  },
  {
    key: 'admin', name: 'Admin Panel', icon: Shield, category: 'utility', path: '/admin', live: true,
    description: 'Users, approvals, roles & permissions, departments, audit & registry',
    // Visible to Super Admins and anyone holding a `view` grant on any Admin Panel feature.
    perm: (hasPerm, user, auth) => Boolean(auth?.canModule?.('ADMIN_PANEL', 'view')),
    nav: [
      { group: 'Access', items: [
        { label: 'User Management', path: '/admin/users', icon: UsersRound, perm: adminFeature('userManagement') },
        { label: 'Approvals', path: '/admin/approvals', icon: UserCheck, perm: adminFeature('approvals') },
        { label: 'Roles & Permissions', path: '/admin/roles-permissions', icon: ShieldCheck, perm: adminFeature('rolesPermissions') },
        { label: 'Departments', path: '/admin/departments', icon: Building2, perm: adminFeature('departments') },
      ] },
      { group: 'System', items: [
        { label: 'Settings & Masters', path: '/admin/settings-masters', icon: SlidersHorizontal, perm: adminFeature('settingsMasters') },
        { label: 'Audit Log', path: '/admin/audit-log', icon: ScrollText, perm: adminFeature('auditLog') },
        { label: 'Registry', path: '/admin/registry', icon: BookMarked, perm: adminFeature('moduleRegistry') },
      ] },
    ],
  },
  {
    key: 'settings', name: 'Settings', icon: Settings, category: 'utility', path: '/sales/settings', live: true,
    description: 'Company thresholds, reminders & templates',
    perm: (hasPerm) => hasPerm('settings', 'view'),
    launcherOnly: true, // opens inside the Sales workspace
  },
];

export const canSeeApp = (app, hasPerm, user, auth) => (app.perm ? app.perm(hasPerm, user, auth) : true);
export const itemAllowed = (item, hasPerm, user, auth) => {
  if (!item.perm) return true;
  if (typeof item.perm === 'function') return Boolean(item.perm(hasPerm, user, auth));
  return hasPerm(item.perm[0], item.perm[1]);
};

/** Workspace config for the current pathname (longest matching app path). */
export const appForPath = (pathname) => {
  const matches = APPS.filter((a) => !a.launcherOnly &&
    (pathname === a.path || pathname.startsWith(`${a.path}/`)));
  matches.sort((a, b) => b.path.length - a.path.length);
  return matches[0] || null;
};

/** Label of the nav item matching the current pathname (for breadcrumbs). */
export const currentNavLabel = (app, pathname) => {
  if (!app?.nav) return app && !app.live ? 'Coming Soon' : '';
  let best = null;
  for (const group of app.nav) {
    for (const item of group.items) {
      if (item.soon || !item.path) continue;
      if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
        if (!best || item.path.length > best.path.length) best = item;
      }
    }
  }
  return best?.label || '';
};

// Pinned-tile preference persistence. NOTE: this stores ONLY app keys the user
// pinned on the launcher (a cosmetic UI preference) — never tokens, credentials
// or personal data — so plain localStorage is appropriate here.
const PIN_KEY = 'erp:pinned-apps';

export const getPinnedKeys = () => {
  try {
    const v = JSON.parse(localStorage.getItem(PIN_KEY) || '[]');
    return Array.isArray(v) ? v : [];
  } catch (err) {
    logger.warn('Pinned apps preference unreadable — starting fresh:', err);
    return [];
  }
};

export const setPinnedKeys = (keys) => {
  try {
    localStorage.setItem(PIN_KEY, JSON.stringify(keys));
  } catch (err) {
    logger.warn('Could not persist pinned apps preference:', err);
  }
};
